import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import _ from "lodash";
import {
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
} from "@material-ui/core";
import { useTranslate } from "react-polyglot";
import { useCourseProvider } from "./CourseProvider.jsx";
import { useHttp } from "../Common/HttpProvider.jsx";
import { useAuth } from "../Auth/AuthProvider.jsx";
import useEffectAsync from "../../hooks/useEffectAsync.js";
import useEffectWithPreviousValues from "../../hooks/useEffectWithPreviousValues.js";
import useExtractRouteParamInt from "../../hooks/useExtractRouteParamInt.js";
import { largeRewardItemTypes, HOMEWORK_STATUS } from "../../constants.js";
import { useBaseData } from "./BaseDataProvider.jsx";
import FeedbackDialog from "../../SharedComponents/FeedbackDialog/FeedbackDialog.jsx";

const useDialogStyles = makeStyles(() => ({
  dialog: {
    borderRadius: 10,
  },
  actionsRoot: {
    justifyContent: "center",
    paddingBottom: 16,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 15,
    paddingBottom: 16,
  },
  dialogMessage: {
    whiteSpace: "pre-line",
  },
}));

export const DEFAULT_LEARNED_PATCH = Object.freeze({
  learned: true,
  skip: false,
  attempt: 1,
  payload: {},
});

/**
 * @callback RequestOtherExercise
 * @param {Number} direction - if direction > -1 then function request next exercise, previous exercise otherwise
 * @returns {Promise<void>}
 */

/**
 * @callback RequestOtherWord
 * @param {Number} direction - if direction > -1 then function request next word, previous word otherwise
 * @returns {Promise<void>}
 */

/**
 * @typedef {Object} LearnProcessProviderContext
 * @property {?WordLearningResponse} word
 * @property {?Number} specifiedWordId
 * @property {?VideosResponse} videos
 * @property {?ExerciseResponse} exercise
 * @property {?Number} specifiedExerciseId
 * @property {?VideoLearningResponse} learnVideoResult
 * @property {Boolean} isExpertMode
 * @property {Boolean} waitAIReview
 * @property {Number} selectedVideoIndex
 * @property {Boolean} areWordsExpanded
 * @property {Number} videoWordPatching
 * @property {Number[]} learnedVideos
 * @property {FnBooleanVoid} learnWord
 * @property {FnAsyncNumberBooleanVoid} learnVideoWord
 * @property {FnAsyncNumberVoid} learnVideo
 * @property {FnBooleanVoid} setExpertMode
 * @property {Function} setSelectedVideoIndex
 * @property {Function} setWordsExpanded
 * @property {FnPatchExercise} patchExercise
 * @property {Function} skipExercise
 * @property {Function} skipWaitReview
 * @property {FnAsyncStringOptVoid} userIsWrong
 * @property {RequestOtherExercise} requestOtherExercise
 * @property {RequestOtherWord} requestOtherWord
 * @property {FnAsyncStringVoid} saveUsersAnswer
 * @property {Rewards} rewards
 * @property {function} resetRewards
 * @property {function} restartExam
 */

/**
 * @type {React.Context<LearnProcessProviderContext>}
 */
const learnContext = createContext({
  word: null,
  videos: null,
  exercise: null,
  specifiedWordId: null,
  specifiedExerciseId: null,
  learnVideoResult: null,
  isExpertMode: false,
  selectedVideoIndex: 0,
  areWordsExpanded: false,
  videoWordPatching: -1,
  learnedVideos: [],
  rewards: null,
  waitAIReview: false,
  /** @type FnBooleanVoid */ learnWord: () => {},
  /** @type {FnAsyncNumberBooleanVoid} */ learnVideoWord: () => {},
  /** @type {FnAsyncNumberVoid} */ learnVideo: () => {},
  /** @type FnBooleanVoid */ setExpertMode: () => {},
  setSelectedVideoIndex: () => {},
  setWordsExpanded: () => {},
  setRewards: () => {},
  /** @type FnPatchExercise */ patchExercise: () => {},
  skipExercise: () => {},
  skipWaitReview: () => {},
  /** @type FnAsyncStringOptVoid */ userIsWrong: () => {},
  /** @type {RequestOtherExercise} */ requestOtherExercise: () => {},
  /** @type {RequestOtherWord} */ requestOtherWord: () => {},
  /** @type {FnAsyncStringVoid} */ saveUsersAnswer: () => {},
  /** @type {function} */ resetRewards: () => {},
  restartExam: () => {},
});

/**
 * @returns {LearnProcessProviderContext}
 */
export const useLearnProcess = () => useContext(learnContext);

/**
 * @param {React.ReactNode} children
 * @returns {*}
 * @constructor
 */
const LearnProcessProvider = ({ children }) => {
  const t = useTranslate();
  const { get, patch, post } = useHttp();
  const history = useHistory();
  const location = useLocation();
  const { isViewExamDeck } = location.state || {};
  const classes = useDialogStyles();
  const { currentDeck, currentBranch, updateCourseData } = useCourseProvider();
  const { rewardsData, resetRewardsData } = useAuth();
  const { company, userInfo } = useBaseData();
  const [isExpertMode, setExpertMode] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [areWordsExpanded, setWordsExpanded] = useState(false);
  const [videoWordPatching, setVideoWordPatching] = useState(-1);
  const [learnedVideos, setLearnedVideos] = useState(/** @type {Number[]} */ []);
  const wordId = useExtractRouteParamInt("/learn/:deckId/:wordId", "wordId");
  const exerciseId = useExtractRouteParamInt("/learn/:deckId/exercise/:exerciseId", "exerciseId");
  // Learning
  const [word, setWord] = useState(/** @type ?WordLearningResponse */ null);
  const [videos, setVideos] = useState(/** @type ?VideosResponse */ null);
  const [learnVideoResult, setLearnVideoResult] = useState(/** @type ?VideoLearningResponse */ null);
  const [exercise, setExercise] = useState(/** @type {?ExerciseResponse} */ null);
  const [waitAIReview, setWaitAIReview] = useState(/** @type {boolean} */ false);
  // rewards
  const [rewards, setRewards] = useState(/** @type {?Rewards} */ null);
  const [reviewRewards, setReviewRewards] = useState(/** @type {?LargeRewardItem[]} */ null);
  const [currentReviewData, setCurrentReviewData] = useState(
    /** @type {?{ data: DataToProvideReview, instructionText: String, resource: 'company'|'subject' }} */ null,
  );

  const timeoutRef = useRef(null);

  // запросы на оценку компании и предмета приходят в ваиде наград миссии. Необходимо их отделить в отдельную переменную reviewRewards
  /** @type {FnObjectVoid} */
  const separateRewards = (rewards) => {
    if (rewards) {
      setRewards(rewards);
      const newReviewRewards = rewards.large?.items.filter(
        (item) =>
          item.type === largeRewardItemTypes.get_review_company ||
          item.type === largeRewardItemTypes.get_review_subject,
      );
      if (newReviewRewards?.length > 0) {
        setReviewRewards(newReviewRewards);
      }
    }
  };

  /** @param {Number} deckId */
  const requestRandomWord = async (deckId) => {
    const resp = await get(`words/random/${deckId}`);
    if (resp) {
      setWord(resp);
    }
  };

  /**
   * @param {Number} wordId
   * @param {Number} deckId
   */
  const requestWordById = async (wordId, deckId) => {
    const resp = await get(`words/${wordId}/${deckId}`);
    if (resp) {
      setWord(resp);
    }
  };
  /** @type {RequestOtherWord} */
  const requestOtherWord = async (direction) => {
    let lastSegment = "previous";
    if (direction > -1) {
      lastSegment = "next";
    }
    const resp = await get(`words/${word.word.id}/${currentDeck.id}/${lastSegment}`);
    if (resp) {
      setWord(resp);
    }
  };

  /** @type FnAsyncNumberNumberBooleanBoolean */
  const postWordLearningResult = async (wordId, deckId, learned) => {
    const resp = await patch(`words/${wordId}/${deckId}`, { learned });
    if (resp) {
      setWord(resp);
      separateRewards(resp.rewards);
      return true;
    } else {
      return false;
    }
  };

  //  При отправке на сервер времени, проведенного пользователем в приложении (сессий), приходят награды миссий.
  //  Если такие награды есть - устанавливаем их для отображения поп-апа с поздравлениями.
  useEffect(() => {
    if (rewardsData) {
      separateRewards(rewardsData);
    }
  }, [rewardsData]);

  // Формирует данные для диалога отзыва при появлении соответствующей «крупной» награды.
  // Следит за изменением списка reviewRewards:
  // - Берёт первую награду.
  // - В зависимости от типа (оценка компании/предмета) собирает объект reviewData:
  //   * data — целевая сущность (company | subject) с полями { id, name }.
  //   * instructionText — локализованный текст инструкции.
  //   * resource — строковый ключ ресурса: "company" | "subject".
  // - Устанавливает currentReviewData, чтобы отобразить модальное окно с просьбой оставить отзыв.
  // Зависимость [reviewRewards] гарантирует запуск эффекта только при изменении списка наград.
  useEffect(() => {
    if (reviewRewards?.length > 0) {
      const currentRewards = reviewRewards[0];
      let reviewData = null;
      switch (currentRewards.type) {
        case largeRewardItemTypes.get_review_company:
          reviewData = {
            data: {
              id: company.id,
              name: t("companyReviewTitle"),
            },
            instructionText: t("companyReviewText"),
            resource: "company",
          };
          break;
        case largeRewardItemTypes.get_review_subject:
          reviewData = {
            data: {
              id: userInfo.subject.id,
              name: t("subjectReviewTitle"),
            },
            instructionText: t("subjectReviewText"),
            resource: "subject",
          };
          break;
        default:
          break;
      }

      setCurrentReviewData(reviewData);
    }
  }, [reviewRewards]);

  // update deck id in path when branch is changing
  useEffectWithPreviousValues(
    /** @param {Branch} prevBranch */
    ([prevBranch]) => {
      const decks = currentBranch && currentBranch.decks;
      if (!wordId && prevBranch && currentBranch && prevBranch.id !== currentBranch.id && decks && decks.length > 0) {
        history.push(`/learn/${decks[0].id}`);
      }
    },
    [currentBranch],
  );

  // request first word
  useEffectWithPreviousValues(
    /** @param {Deck} prevDeck */
    ([prevDeck]) => {
      // Если в уроке нет слов (по items), не инициализируем процесс изучения слов
      if (!currentDeck || !currentDeck.open || currentDeck.words.items.length === 0) {
        return;
      }
      if (wordId) {
        requestWordById(wordId, currentDeck.id).then();
      } else if (!word || prevDeck.id !== currentDeck.id) {
        requestRandomWord(currentDeck.id).then();
      } else {
        requestWordById(word.word.id, currentDeck.id).then();
      }
    },
    [currentDeck, wordId],
  );

  // Необходим при проверке ИИ выполненного ДЗ. Если при отправке ДЗ функцией patchExercise приходит waitAIReview === true
  // необходимо периодически запрашивать текущее упражнение и отслеживать статус, проверено ли ИИ.
  useEffectAsync(async () => {
    const id = exercise?.exercise?.id || exerciseId;
    if (!waitAIReview || !id || !currentDeck?.id) {
      return;
    }

    /** */
    const getCurrentExercise = async () => {
      const silent = true;
      const resp = await get(`exercises/${id}/${currentDeck.id}/current`, null, null, silent);

      if (resp?.exercise?.data?.answer?.homework?.status !== HOMEWORK_STATUS.wait_review) {
        setWaitAIReview(false);
        setExercise(resp);
        updateCourseData();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        timeoutRef.current = setTimeout(getCurrentExercise, 7000);
      }
    };
    getCurrentExercise();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [waitAIReview, exerciseId, exercise?.exercise?.id, currentDeck?.id]);

  //request videos
  useEffectAsync(async () => {
    // Если в уроке нет видео (по items), не запрашиваем список видео
    if (!currentDeck || !currentDeck.open || currentDeck.videos.items.length === 0) {
      return;
    }
    /** @type {VideosResponse} */
    const resp = await get(`videos/list/${currentDeck.id}`);
    if (resp) {
      setVideos(resp);
    }
  }, [currentDeck, word, learnVideoResult]);

  //request random exercise
  useEffectWithPreviousValues(
    /** @param {?Deck} prevDeck */
    ([prevDeck]) => {
      const asyncFun = async () => {
        if (!currentDeck || !currentDeck.open || currentDeck.exercises.items.length === 0 || exerciseId) {
          return;
        }
        if (prevDeck && prevDeck.id === currentDeck.id) {
          return;
        }
        if (isViewExamDeck) {
          /** @type {ExerciseResponse} */
          const resp = await get(`exercises/random/${currentDeck.id}/viewExamResult`);
          if (resp) {
            setExercise(resp);
          }
        } else {
          /** @type {ExerciseResponse} */
          const resp = await get(`exercises/random/${currentDeck.id}`);
          if (resp) {
            setExercise(resp);
          }
        }
      };
      asyncFun().then();
    },
    [currentDeck, exerciseId],
  );

  //request exercise by id specified in path
  useEffectAsync(async () => {
    if (!currentDeck || !currentDeck.open || currentDeck.exercises.items.length === 0) {
      return;
    }
    if (exerciseId) {
      // При просмотре результатов сданного экзамена (isViewExamDeck) запрашиваем упражнение
      // через endpoint viewExamResult, чтобы избежать повторного старта экзамена на сервере.
      const endpoint = isViewExamDeck
        ? `exercises/${exerciseId}/${currentDeck.id}/viewExamResult`
        : `exercises/${exerciseId}/${currentDeck.id}`;
      /** @type {ExerciseResponse} */
      const resp = await get(endpoint);
      if (resp) {
        setExercise(resp);
      }
    }
  }, [currentDeck, exerciseId, isViewExamDeck]);

  /** @type {RequestOtherExercise} */
  const requestOtherExercise = async (direction) => {
    let lastSegment = "previous";
    if (direction > -1) {
      lastSegment = "next";
    }
    const resp = await get(`exercises/${exercise.exercise.id}/${currentDeck.id}/${lastSegment}`);
    if (resp) {
      setExercise(resp);
    }
  };

  /**
   * @returns {Promise<void>}
   */
  const restartExam = async () => {
    /** @type {ExerciseResponse} */
    const resp = await get(`exercises/random/${currentDeck.id}`);
    if (resp) {
      setExercise(resp);
      // обновляем данные курса, чтобы дерево упражнений не показывало прогресс (галочки, проценты) прошлой попытки
      updateCourseData();
    }
  };

  /** @type FnBooleanVoid */
  const learnWord = async (learned) => {
    const resp = await postWordLearningResult(word.word.id, currentDeck.id, learned);
    if (resp) {
      if (wordId) {
        history.push(`/learn/${currentDeck.id}`);
      }
      updateCourseData();
    }
  };

  /** @type {FnAsyncNumberBooleanVoid} */
  const learnVideoWord = async (wordId, learned) => {
    setVideoWordPatching(wordId);
    const result = await postWordLearningResult(wordId, currentDeck.id, learned);
    if (result) {
      const currentWordIndex = videos.videos[selectedVideoIndex].words.findIndex((word) => word.id === wordId);
      videos.videos[selectedVideoIndex].words[currentWordIndex].learned = true;
      setVideos({ ...videos });
      updateCourseData();
    }
    setVideoWordPatching(-1);
  };

  /**
   * Сохраняет просмотр видео урока и обновляет прогресс обучения.
   *
   * @param {number} videoId - Идентификатор видео.
   * @returns {Promise<void>} Завершение сохранения отметки просмотра.
   */
  const learnVideo = async (videoId) => {
    if (learnedVideos.findIndex((id) => id === videoId) < 0) {
      setLearnedVideos((currentIds) => [...currentIds, videoId]);
    } else {
      return;
    }
    /** @type {VideoLearningResponse} */
    const resp = await patch(`videos/${videoId}/${currentDeck.id}`, { learned: true });
    if (resp) {
      setLearnVideoResult(resp);
      updateCourseData();
      separateRewards(resp.rewards);
    } else {
      setLearnedVideos((currentIds) => currentIds.filter((id) => id !== videoId));
    }
  };

  /** @type {FnPatchExercise} */
  const patchExercise = async (body) => {
    const resp = await patch(`exercises/${exercise.exercise.id}/${currentDeck.id}`, body);
    if (resp) {
      if (resp.waitAIReview && userInfo.useAiToCheckHomeworks) {
        setWaitAIReview(true);
        setExercise(resp);
      } else {
        setExercise(resp);
        updateCourseData();
      }
      separateRewards(resp.rewards);
    }
  };

  /** */
  const skipExercise = () => {
    patchExercise({
      learned: false,
      skip: true,
      attempt: 1,
      payload: {},
    }).then();
  };

  /** */
  const skipWaitReview = () => {
    setWaitAIReview(false);
    requestOtherExercise(0).then();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  /**
   * @param {string} [answerValue]
   * @returns {Promise<void>}
   */
  const userIsWrong = async (answerValue) => {
    await patchExercise({
      learned: false,
      skip: false,
      attempt: 1,
      payload: { answerValue },
    });
  };

  /** @type {FnAsyncStringVoid} */
  const saveUsersAnswer = async (answerValue) => {
    await post(
      `exercises/saveAnswer/${exercise.exercise.id}/${currentDeck.id}`,
      { value: answerValue },
      null,
      {},
      true,
    );
  };

  /** */
  const resetRewards = () => {
    setRewards(null);
    resetRewardsData();
  };

  /** @type {LearnProcessProviderContext} */
  const value = {
    word,
    videos,
    exercise,
    specifiedExerciseId: exerciseId,
    specifiedWordId: wordId,
    learnVideoResult,
    isExpertMode,
    selectedVideoIndex,
    areWordsExpanded,
    videoWordPatching,
    learnedVideos,
    rewards,
    learnWord,
    learnVideoWord,
    learnVideo,
    waitAIReview,
    setRewards,
    setExpertMode,
    setSelectedVideoIndex,
    setWordsExpanded,
    patchExercise,
    skipExercise,
    skipWaitReview,
    userIsWrong,
    requestOtherExercise,
    requestOtherWord,
    saveUsersAnswer,
    resetRewards,
    restartExam,
  };
  return (
    <learnContext.Provider value={value}>
      {children}
      {currentReviewData && (
        <FeedbackDialog
          data={currentReviewData.data}
          instructionText={currentReviewData.instructionText}
          resource={currentReviewData.resource}
          onClose={() => {
            setCurrentReviewData(null);
            const cloneRewards = _.cloneDeep(reviewRewards);
            cloneRewards.shift();
            setReviewRewards(cloneRewards);
          }}
          open={!!currentReviewData}
          hideDefaultInstruction
        />
      )}
      <Dialog
        maxWidth="xs"
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        open={waitAIReview}
        onClose={() => skipWaitReview()}
        className={classes.dialog}
      >
        <DialogTitle>{t("homeworks.waitAIReview")}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography className={classes.dialogMessage}>{t("homeworks.checkingHomework")}</Typography>
          <CircularProgress />
        </DialogContent>
        <DialogActions className={classes.actionsRoot}>
          <Button onClick={() => skipWaitReview()} color="secondary" autoFocus variant="contained">
            {t("homeworks.dontWait")}
          </Button>
        </DialogActions>
      </Dialog>
    </learnContext.Provider>
  );
};

export default LearnProcessProvider;
